import type { Action, GameEvent } from "./engine.js";
import type { Activity, DeviceProfile, GameState, PromptKind, PromptOutcome, NudgeType } from "./types.js";

export const PROTOCOL_VERSION = 1;
export const HEARTBEAT_MS = 25_000;
/** No message (incl. pong) for this long = dead connection. */
export const DEAD_AFTER_MS = 60_000;

export interface DeviceInfo {
  id: string;
  name: string;
  profile: DeviceProfile;
}

/**
 * Actions a client may send directly (chips, settings, scheduler bookkeeping).
 * Anything else goes through conversation.
 */
export type ClientAction = Extract<
  Action,
  {
    type:
      | "complete_quest"
      | "postpone_quest"
      | "drop_quest"
      | "reminder_ack"
      | "rate_week"
      | "log_activity"
      | "shopping_toggle"
      | "shopping_remove"
      | "shopping_clear"
      | "update_settings"
      | "onboard"
      | "set_look"
      | "buy_item"
      | "prompt_shown"
      | "prompt_outcome"
      | "mute"
      | "unmute"
      | "set_mode"
      | "forget_fact"
      | "debug";
  }
>;

export const CLIENT_ACTIONS: ReadonlySet<string> = new Set([
  "complete_quest",
  "postpone_quest",
  "drop_quest",
  "reminder_ack",
  "rate_week",
  "log_activity",
  "shopping_toggle",
  "shopping_remove",
  "shopping_clear",
  "update_settings",
  "onboard",
  "set_look",
  "buy_item",
  "prompt_shown",
  "prompt_outcome",
  "mute",
  "unmute",
  "set_mode",
  "forget_fact",
  "debug",
]);

export type ClientMessage =
  | { t: "hello"; v: number; device: DeviceInfo; tz: string; visible: boolean }
  | { t: "say"; id: string; text: string }
  | { t: "act"; id?: string; action: ClientAction }
  | { t: "call_here" }
  | { t: "visible"; visible: boolean }
  | { t: "ping" };

export interface ReplyPayload {
  text: string;
  /** Short mute-style answers end the conversation (no listening). */
  endConversation: boolean;
  /** Ask again (the reply ends with a question). */
  question: boolean;
  results: { type: string; ok: boolean; result: string }[];
  source: "quick" | "llm" | "fallback" | "limit";
}

export type ServerMessage =
  | { t: "state"; rev: number; state: GameState }
  | { t: "events"; rev: number; events: GameEvent[]; origin: string | null }
  | { t: "reply"; re: string; reply: ReplyPayload }
  | { t: "ack"; re: string; ok: boolean; result: string }
  | { t: "error"; code: string; message: string }
  | { t: "pong" };

export interface Outbound {
  to: "all" | string;
  msg: ServerMessage;
}

export interface ExportBundle {
  format: "squire-profile";
  version: 1;
  exportedAt: string;
  state: GameState;
  history: {
    activities: Activity[];
    quests: GameState["quests"];
    reminders: GameState["reminders"];
    messages: GameState["messages"];
    ratings: Record<string, number>;
  };
}

/** Narrow unknown JSON from the wire. Returns null when the message is malformed. */
export function parseClientMessage(raw: unknown): ClientMessage | null {
  let m: unknown = raw;
  if (typeof raw === "string") {
    if (raw.length > 20_000) return null;
    try {
      m = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!m || typeof m !== "object") return null;
  const o = m as Record<string, unknown>;
  switch (o.t) {
    case "hello": {
      const d = o.device as Record<string, unknown> | undefined;
      if (!d || typeof d.id !== "string" || !d.id || d.id.length > 64) return null;
      return {
        t: "hello",
        v: Number(o.v) || 1,
        device: {
          id: d.id,
          name: typeof d.name === "string" ? d.name.slice(0, 30) : "urządzenie",
          profile: d.profile === "pocket" ? "pocket" : "wall",
        },
        tz: typeof o.tz === "string" ? o.tz.slice(0, 64) : "Europe/Warsaw",
        visible: o.visible !== false,
      };
    }
    case "say":
      if (typeof o.text !== "string" || !o.text.trim()) return null;
      return { t: "say", id: typeof o.id === "string" ? o.id.slice(0, 40) : "", text: o.text.slice(0, 1000) };
    case "act": {
      const a = o.action as Record<string, unknown> | undefined;
      if (!a || typeof a.type !== "string" || !CLIENT_ACTIONS.has(a.type)) return null;
      return {
        t: "act",
        id: typeof o.id === "string" ? o.id.slice(0, 40) : undefined,
        action: a as unknown as ClientAction,
      } as ClientMessage;
    }
    case "call_here":
      return { t: "call_here" };
    case "visible":
      return { t: "visible", visible: Boolean(o.visible) };
    case "ping":
      return { t: "ping" };
    default:
      return null;
  }
}

/** Which device shows due reminders: the companion's device, or wall devices while a pocket companion is asleep. */
export function presentsReminders(state: GameState, deviceId: string, profile: DeviceProfile): boolean {
  if (state.location.deviceId === deviceId) return true;
  if (profile !== "wall") return false;
  const host = state.devices.find((d) => d.id === state.location.deviceId);
  return state.location.profile === "pocket" && !host?.visible;
}

export type { PromptKind, PromptOutcome, NudgeType };
